/**
 * M2 bridge, career (M3 plan §7 item 1, R22c): the `CareerPort` over M2's `Career`, which retires M3's stand-in.
 *
 * - **Wallet:** `credits()` is the career wallet. `earn` adds through `withWallet` and counts `stats.earned` (chart
 *   sales, caches and base rewards are earnings); `spend` refuses (false, nothing changes) past the wallet.
 * - **Relay parts:** the three relay goods in the career's goods block `cargo.parts` (D-TBD 40, SY-2a), through
 *   R22b's `addParts`, `removeParts` and `partsThatFit`. Only relay part ids pass (`isRelayPartId`; the other goods
 *   are not the Reach's), and the port counts whole units (goods are floats). Adds clamp to what fits and removals
 *   to what is carried, like the stand-in.
 * - **Hold:** `cargoFreeT()` is M2's free hold, so ore, salvage and parts share it.
 * - **Scanner Mk:** read from the fitted `scanner` slot (`scanner.mkN`). `setScannerMk` fits `scanner.mkN` and the
 *   old scanner goes to the locker; a fit the power plant cannot carry puts the new scanner in the locker instead.
 * - **Strategic Parts tab (economy plan SY-2e):** when the access carries the career runtime's goods trade and a
 *   strategic market runs, `goodQuote` prices a relay part on the station's curve (with its stock) and `buyGood`
 *   buys it out of the station's stock through `CareerRuntime.buyGood` (quote → commit → book). With no strategic
 *   market `goodQuote` is null and the relink command keeps the stand-in `partPrices`.
 * - **Death:** `onPlayerDeath` is a no-op, because M2's `applyDeath` already empties the parts block (the goods
 *   go into the death pod, SY-2b).
 * - **Stand-in import:** `importStandIn` moves a pre-bridge Reach save's stand-in once (credits to the wallet, parts
 *   into the hold, a better scanner fitted), then sets `state.standIn = null`. Parts that do not fit are refunded
 *   at the stand-in prices. Only a loaded save imports: a new game's stand-in only holds its start values.
 *
 * Every write goes through one `CareerAccess.apply` (the career runtime's `applyDevCommand`: commit, cargo mirror,
 * publish), so the career stays immutable. Writes need an active career; before one starts they are refused.
 * Reads allocate nothing (the HUD and the scan radius call them per frame).
 */
import { addParts, partsThatFit, removeParts } from '../../career/cargo'
import { powerBudget, powerDraw } from '../../career/fitting'
import { EPSILON, GOOD_NAMES, cargoFreeT, withWallet } from '../../career/ops'
import { goodT, isRelayPartId } from '../../career/parts'
import type { CmdErrorCode, CmdResult } from '../../career/result'
import type { CareerRuntime } from '../../career/runtime'
import type { Career } from '../../career/types'
import { logger } from '../../core/logger'
import type { M2Data, ModuleId } from '../../data/m2Schemas'
import type { CareerPort, PartQuote, SettlingResult } from '../../reach/ports'
import { loadStandInConfig } from '../../reach/standin'
import { PART_IDS, type PartId, type ReachCommandError, type ReachCommandResult, type ReachState, type StandInState } from '../../reach/types'
import type { CmdOutcome } from '../../ui/contracts'

type ScannerMk = 1 | 2 | 3

/** The career runtime's strategic goods trade by station id (SY-2c), as the Parts tab uses it (SY-2e). */
export interface GoodsAccess {
  /** Whether a strategic market runs; false keeps the Reach's stand-in prices. */
  goodsMarketRuns(): boolean
  quoteGood: CareerRuntime['quoteGood']
  buyGood: CareerRuntime['buyGood']
}

/** Reads and writes M2's career. A write is a pure `Career → Career` change; a throw refuses it. */
export interface CareerAccess {
  career(): Career
  apply(name: string, cmd: (career: Career) => Career): CmdOutcome
  /** The strategic goods trade (SY-2e), when the career service exposes it. */
  goods?: GoodsAccess
}

/** What `careerAccess` wraps: the career runtime's write hook, and its goods trade when the service carries it. */
export type CareerAccessSource = Pick<CareerRuntime, 'career' | 'applyDevCommand'> & Partial<GoodsAccess>

/** The access over M2's career runtime: writes go through `applyDevCommand`, which already commits and publishes. */
export function careerAccess(runtime: CareerAccessSource): CareerAccess {
  const { goodsMarketRuns, quoteGood, buyGood } = runtime
  const goods: GoodsAccess | undefined =
    goodsMarketRuns !== undefined && quoteGood !== undefined && buyGood !== undefined ? { goodsMarketRuns, quoteGood, buyGood } : undefined
  return {
    career: () => runtime.career(),
    apply: (name, cmd) => runtime.applyDevCommand(name, cmd),
    goods,
  }
}

/** A career refusal as a Reach refusal: money and hold map to theirs, a settling trade to busy, the rest keep their text. */
function reachError(code: CmdErrorCode): ReachCommandError {
  switch (code) {
    case 'insufficientFunds':
      return 'funds'
    case 'cargoFull':
      return 'cargo'
    case 'notReady':
      return 'busy'
    default:
      return 'invalid'
  }
}

function reachRefusal(error: ReachCommandError, message: string): ReachCommandResult {
  return { ok: false, error, message }
}

export interface BridgeCareerDeps {
  access: CareerAccess
  /** M2's module data (the scanner fit checks the power budget). */
  data: Pick<M2Data, 'modules'>
}

/** What a stand-in import moved into M2's career. */
export interface StandInImport {
  /** Stand-in credits added to the wallet. */
  credits: number
  /** Units of each part moved into the hold. */
  parts: Record<PartId, number>
  /** Credits paid for the parts that did not fit, at the stand-in prices (also added to the wallet). */
  refunded: number
  /** The stand-in scanner Mk moved over (fitted, or into the locker), or null when M2's was as good. */
  scannerMk: ScannerMk | null
}

const SCANNER_MODULE: Readonly<Record<ScannerMk, ModuleId>> = Object.freeze({ 1: 'scanner.mk1', 2: 'scanner.mk2', 3: 'scanner.mk3' })

/** The Mk of the fitted scanner module (`scanner.mkN`). Allocation-free. */
export function fittedScannerMk(career: Career): ScannerMk {
  switch (career.ship.fitted.scanner) {
    case 'scanner.mk3':
      return 3
    case 'scanner.mk2':
      return 2
    default:
      return 1
  }
}

/**
 * Fits `scanner.mk<mk>` (a new module) in the scanner slot; the old scanner goes to the locker. When the fit would
 * draw more than the power budget, the new scanner goes to the locker instead (fit it at outfitting later).
 */
export function withScanner(career: Career, data: Pick<M2Data, 'modules'>, mk: ScannerMk): Career {
  const id = SCANNER_MODULE[mk]
  const old = career.ship.fitted.scanner
  if (old === id) return career
  const fitted = { ...career.ship.fitted, scanner: id }
  if (powerDraw(fitted, data) > powerBudget(fitted, data)) {
    logger.info('bridge: scanner stored in the locker (power budget)', { module: id })
    return { ...career, ship: { ...career.ship, locker: [...career.ship.locker, id] } }
  }
  return { ...career, ship: { ...career.ship, fitted, locker: [...career.ship.locker, old] } }
}

/** A whole, non-negative quantity (NaN and negatives are 0), as the stand-in counts. */
function wholeQty(qty: number): number {
  return Number.isFinite(qty) ? Math.max(0, Math.floor(qty)) : 0
}

/** The value of a command result, or a throw that `CareerAccess.apply` turns into a refusal. */
function valueOf(result: CmdResult<Career>): Career {
  if (!result.ok) throw new Error(result.error.message)
  return result.value
}

/** Adds `amount` credits and counts them as earned. */
function withEarned(career: Career, amount: number): Career {
  const next = withWallet(career, amount)
  return { ...next, stats: { ...next.stats, earned: next.stats.earned + amount } }
}

export function createBridgeCareer(deps: BridgeCareerDeps): CareerPort {
  const { access, data } = deps
  const write = (name: string, cmd: (career: Career) => Career): boolean => {
    const out = access.apply(name, cmd)
    if (!out.ok) logger.warn(`bridge: career write ${name} refused`, out.error)
    return out.ok
  }
  return {
    credits: () => access.career().wallet,
    earn(amount: number, reason: string): void {
      if (!(amount > 0) || !Number.isFinite(amount)) return
      write(`reach earn (${reason})`, (c) => withEarned(c, amount))
    },
    spend(amount: number, reason: string): boolean {
      if (!(amount >= 0) || !Number.isFinite(amount) || amount > access.career().wallet + EPSILON) return false
      if (amount === 0) return true
      return write(`reach spend (${reason})`, (c) => withWallet(c, -amount))
    },
    cargo: (part: PartId) => wholeQty(goodT(access.career().cargo.parts, part)),
    cargoFreeT: () => cargoFreeT(access.career()),
    addCargo(part: PartId, qty: number): number {
      if (!isRelayPartId(part)) return 0
      const n = Math.min(wholeQty(qty), partsThatFit(access.career(), part))
      if (n <= 0) return 0
      return write(`reach cargo +${n} ${part}`, (c) => valueOf(addParts(c, part, n))) ? n : 0
    },
    removeCargo(part: PartId, qty: number): number {
      if (!isRelayPartId(part)) return 0
      const n = Math.min(wholeQty(qty), wholeQty(goodT(access.career().cargo.parts, part)))
      if (n <= 0) return 0
      return write(`reach cargo -${n} ${part}`, (c) => valueOf(removeParts(c, part, n))) ? n : 0
    },
    scannerMk: () => fittedScannerMk(access.career()),
    flownShipDefId: () => access.career().ship.defId,
    setScannerMk(mk: ScannerMk): void {
      if (mk !== 1 && mk !== 2 && mk !== 3) return
      if (fittedScannerMk(access.career()) === mk) return
      write(`reach scanner Mk ${mk}`, (c) => withScanner(c, data, mk))
    },
    onPlayerDeath(): void {
      // M2's death rule (applyDeath) empties the parts block; nothing else to do here.
    },
    goodQuote(stationId: string, part: PartId): PartQuote | null {
      const goods = access.goods
      if (goods === undefined || !goods.goodsMarketRuns()) return null
      const one = goods.quoteGood(stationId, part, 1, 'buy')
      if (one === null) return { unit: null, stock: 0 }
      // Asking for everything clamps the quote to what the station can sell now.
      const all = goods.quoteGood(stationId, part, Number.POSITIVE_INFINITY, 'buy')
      return { unit: one.unit, stock: wholeQty(all?.qty ?? 0) }
    },
    buyGood(stationId: string, part: PartId, qty: number): SettlingResult {
      const goods = access.goods
      const label = GOOD_NAMES[part]
      if (goods === undefined || !goods.goodsMarketRuns()) return reachRefusal('unavailable', 'No strategic market here')
      const n = wholeQty(qty)
      if (n < 1) return reachRefusal('invalid', 'Buy at least one part')
      const q = goods.quoteGood(stationId, part, n, 'buy')
      if (q === null) return reachRefusal('invalid', `${label} is not sold here`)
      if (q.clamped) return reachRefusal('invalid', q.qty < 1 ? `No ${label} in stock` : `Only ${wholeQty(q.qty)} ${label} in stock`)
      if (Math.round(q.total) > access.career().wallet + EPSILON) return reachRefusal('funds', 'Not enough credits')
      const settled = goods.buyGood(stationId, part, n).then(
        (out): ReachCommandResult => (out.ok ? { ok: true, message: `Bought ${n} ${label}` } : reachRefusal(reachError(out.error.code), out.error.message)),
        (err: unknown): ReachCommandResult => {
          logger.warn('bridge: strategic relay-part buy failed', err)
          return reachRefusal('busy', 'The market did not answer')
        },
      )
      return { ok: true, message: `Buying ${n} ${label}`, settled }
    },
  }
}

/**
 * The pure stand-in import: parts into the hold (as many whole units as fit; the rest refunded at `partPrices`),
 * credits plus the refund into the wallet (not counted as earned: the money only moves), and the stand-in scanner
 * when it beats the fitted one.
 */
export function importStandInCareer(
  career: Career,
  data: Pick<M2Data, 'modules'>,
  standIn: Readonly<StandInState>,
  partPrices: Readonly<Record<PartId, number>>,
): { career: Career; summary: StandInImport } {
  let next = career
  const parts: Record<PartId, number> = { hull_plating: 0, electronics: 0, drive_parts: 0 }
  let refunded = 0
  for (const part of PART_IDS) {
    const have = wholeQty(standIn.cargo[part])
    const n = Math.min(have, partsThatFit(next, part))
    if (n > 0) next = valueOf(addParts(next, part, n))
    parts[part] = n
    refunded += (have - n) * partPrices[part]
  }
  const credits = Number.isFinite(standIn.credits) ? Math.max(0, standIn.credits) : 0
  if (credits + refunded > 0) next = withWallet(next, credits + refunded)
  let scannerMk: ScannerMk | null = null
  if (standIn.scannerMk > fittedScannerMk(next)) {
    scannerMk = standIn.scannerMk
    next = withScanner(next, data, scannerMk)
  }
  return { career: next, summary: { credits, parts, refunded, scannerMk } }
}

/**
 * Imports a pre-bridge save's stand-in career into M2's career once, then sets `state.standIn = null`. Returns
 * null, and leaves the stand-in in place, when there is none or the career refused the write (no active career).
 */
export function importStandIn(
  access: CareerAccess,
  data: Pick<M2Data, 'modules'>,
  state: ReachState,
  partPrices: Readonly<Record<PartId, number>> = loadStandInConfig().partPrices,
): StandInImport | null {
  const standIn = state.standIn
  if (standIn === null) return null
  const moved: { summary: StandInImport | null } = { summary: null }
  const out = access.apply('reach stand-in import', (c) => {
    const r = importStandInCareer(c, data, standIn, partPrices)
    moved.summary = r.summary
    return r.career
  })
  if (!out.ok || moved.summary === null) {
    if (!out.ok) logger.warn('bridge: stand-in import refused; the stand-in stays until a career is active', out.error)
    return null
  }
  state.standIn = null
  logger.info('bridge: stand-in career imported into the M2 career', moved.summary)
  return moved.summary
}
